import React, { useState, useEffect, useMemo } from "react";
import { Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import useInfiniteScroll from "v2/hooks/useInfiniteScroll";
import SearchInput from "../SuperUserDashboard/SearchInput";
import FilterSelect, { getWearableSortOptions } from "./FilterSelect";
import WearableRow from "./WearableRow";

/**
 * Self-contained wearables list card scoped to a batch. Includes search and an
 * "Allocate" action that opens the supplied handler.
 */
const WearableList = ({ batchId, onAllocate }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortKey, setSortKey] = useState(""); // "" = API default (newest first)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const extraParams = useMemo(
    () => ({ batchId, ordering: sortKey }),
    [batchId, sortKey]
  );

  const { items, loading, hasMore, totalCount, lastItemRef } =
    useInfiniteScroll({
      fetchFn: betaAPI.getWearables,
      search: debouncedSearch,
      pageSize: 20,
      extraParams,
    });

  return (
    <div className="overflow-hidden rounded-xl border border-[#F3F4F6] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#F3F4F6] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("admin.manufacturing.wearableList.title")}
          {totalCount > 0 && (
            <span className="ml-2 text-xs font-normal text-[#9CA3AF]">
              {totalCount.toLocaleString()}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <FilterSelect
            value={sortKey}
            onChange={setSortKey}
            options={getWearableSortOptions(t)}
            allLabel={t("admin.manufacturing.sort.newestFirst")}
          />
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t(
              "admin.manufacturing.wearableList.searchPlaceholder"
            )}
          />
          {onAllocate && (
            <button
              onClick={onAllocate}
              className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold text-white transition duration-150"
              style={{
                background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
              }}
            >
              <Plus size={15} />
              {t("admin.manufacturing.wearableList.register")}
            </button>
          )}
        </div>
      </div>

      <div className="max-h-[560px] overflow-y-auto">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <WearableRow
              key={item.ble_mac}
              wearable={item}
              isLast={isLast}
              ref={isLast ? lastItemRef : null}
            />
          );
        })}

        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 size={20} className="animate-spin text-[#E07A3A]" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-[#6B7280]">
              {debouncedSearch
                ? t("admin.manufacturing.wearableList.emptySearch", {
                    search: debouncedSearch,
                  })
                : t("admin.manufacturing.wearableList.empty")}
            </p>
          </div>
        )}

        {!loading && items.length > 0 && !hasMore && (
          <div className="py-3 text-center text-xs text-[#9CA3AF]">
            {t("admin.manufacturing.wearableList.showingAll", {
              total: totalCount.toLocaleString(),
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WearableList;
